
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { TimeEntryItem } from './TimeEntryItem';
import { AddTimeEntryForm, AddTimeEntryButton } from './AddTimeEntryForm';
import { formatHoursMinutes } from '../../utils/timeUtils';
import { parseLocalDate } from '@/store/weekStore';
import { Project } from '@/hooks/useProjects';

interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  description: string | null;
  is_billable: boolean;
}

interface DayEntry {
  date: string;
  dayName: string;
  entries: TimeEntry[];
  totalMinutes: number;
}

interface DayCardProps {
  day: DayEntry;
  getProjectName: (projectId: string) => string;
  projects: Project[];
  editingEntry: string | null;
  editValues: { start: string; end: string; description: string; date: string; projectId: string };
  onEditStart: (entry: TimeEntry) => void;
  onEditSave: (entryId: string) => void;
  onEditCancel: () => void;
  onEditValueChange: (field: 'start' | 'end' | 'description' | 'date' | 'projectId', value: string) => void;
  onDeleteEntry: (entryId: string) => void;
  onAddEntry?: (date: string, data: {
    projectId: string;
    startTime: string;
    endTime: string;
    description: string;
  }) => Promise<void>;
}

export const DayCard: React.FC<DayCardProps> = ({
  day,
  getProjectName,
  projects,
  editingEntry,
  editValues,
  onEditStart,
  onEditSave,
  onEditCancel,
  onEditValueChange,
  onDeleteEntry,
  onAddEntry,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const toggle = () => setIsOpen(prev => !prev);

  const handleAddClick = () => {
    setIsOpen(true);
    setIsAdding(true);
  };

  const handleAddSave = async (data: {
    projectId: string;
    startTime: string;
    endTime: string;
    description: string;
  }) => {
    if (onAddEntry) {
      await onAddEntry(day.date, data);
      setIsAdding(false);
    }
  };

  return (
    <Card className={day.totalMinutes > 0 ? 'border-blue-200' : ''}>
      <CardHeader className="pb-3 cursor-pointer select-none" onClick={toggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <div className="text-center">
              <div className="text-lg font-bold">{day.dayName}</div>
              <div className="text-sm text-muted-foreground">
                {parseLocalDate(day.date).getDate()}
              </div>
            </div>
            <div className="h-8 w-px bg-border"></div>
            <div>
              <div className="font-semibold">{formatHoursMinutes(day.totalMinutes)}</div>
              <div className="text-sm text-muted-foreground">
                {day.entries.length} {day.entries.length === 1 ? 'entrée' : 'entrées'}
              </div>
            </div>
          </div>
          {day.totalMinutes > 0 && (
            <Badge variant={day.totalMinutes >= 480 ? 'default' : 'secondary'}>
              {day.totalMinutes >= 480 ? 'Journée complète' : 'Partiel'}
            </Badge>
          )}
        </div>
      </CardHeader>

      {isOpen && (
        <CardContent>
          <div className="space-y-3">
            {day.entries.map(entry => (
              <TimeEntryItem
                key={entry.id}
                entry={entry}
                getProjectName={getProjectName}
                projects={projects}
                isEditing={editingEntry === entry.id}
                editValues={editValues}
                onEditStart={onEditStart}
                onEditSave={onEditSave}
                onEditCancel={onEditCancel}
                onEditValueChange={onEditValueChange}
                onDeleteEntry={onDeleteEntry}
              />
            ))}

            {isAdding ? (
              <AddTimeEntryForm
                date={day.date}
                projects={projects}
                onSave={handleAddSave}
                onCancel={() => setIsAdding(false)}
              />
            ) : onAddEntry && (
              <AddTimeEntryButton onClick={handleAddClick} />
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
